/**
 * ResultCard Component (Enhanced)
 * Modern card with TrustScore, tags, and action buttons
 */

import React from 'react';
import clsx from 'clsx';
import { TrustScoreBadge } from './TrustScoreBadge';
import { TagChips, Tag } from './TagChips';
import { ActionButtons, Action, ActionIcons } from './ActionButtons';

export interface ResultCardProps {
  title: string;
  description: string;
  icon?: string;
  trustScore?: number;
  tags?: Tag[];
  url?: string;
  actions?: Action[];
  variant?: 'default' | 'elevated' | 'minimal' | 'google';
  layoutVariant?: 'grid' | 'list';
  date?: string;
  highlighted?: boolean;
  titleMaxLength?: number;
  descriptionMaxLength?: number;
  // Card styling
  cardShowBorder?: boolean;
  cardBorderRadius?: number;
  cardShowShadow?: boolean;
}

// Helper to extract domain from URL
const extractDomain = (url: string): string => {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.replace('www.', '');
  } catch {
    return url;
  }
};

// Helper to truncate text
const truncateText = (text: string, maxLength: number): string => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
};

// Helper to extract title from content or URL
const extractTitle = (title: string, description: string, url?: string): string => {
  // Never return "Details" as title
  if (title && title.toLowerCase() !== 'details') {
    return title;
  }
  
  // Try to use first line of description
  if (description) {
    const firstLine = description.split('\n')[0].trim();
    if (firstLine && firstLine.length > 0 && firstLine.toLowerCase() !== 'details') {
      return firstLine.length > 80 ? firstLine.substring(0, 80) + '...' : firstLine;
    }
  }
  
  // Fallback to domain from URL
  if (url) {
    return extractDomain(url);
  }
  
  // Last resort - TODO: This should be internationalized when i18n is set up
  return 'Sin título';
};

export const ResultCard: React.FC<ResultCardProps> = ({
  title: rawTitle,
  description: rawDescription,
  icon,
  trustScore,
  tags,
  url,
  actions,
  variant = 'default',
  layoutVariant = 'grid',
  date,
  highlighted = false,
  titleMaxLength = 60,
  descriptionMaxLength = 150,
  cardShowBorder = true,
  cardBorderRadius = 12,
  cardShowShadow = true,
}) => {
  // Hide cards without valid URL
  if (!url || url === '#' || url === 'null' || url === '') {
    return null;
  }
  
  // Extract proper title
  const title = extractTitle(rawTitle, rawDescription, url);
  
  // Truncate title and description
  const truncatedTitle = truncateText(title, titleMaxLength);
  const truncatedDescription = truncateText(rawDescription, descriptionMaxLength);
  const handleCardClick = () => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Generate default actions if none provided and URL exists
  const defaultActions: Action[] = url ? [
    {
      label: 'Visitar sitio',
      icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M12 4L4 12M12 4H7m5 0v5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>,
      onClick: () => window.open(url, '_blank', 'noopener,noreferrer'),
      variant: 'outline',
    },
    {
      label: 'Ver más',
      icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <path d="M4.5 6.5L8 10l3.5-3.5" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>,
      onClick: handleCardClick,
      variant: 'ghost',
    },
  ] : [];

  const finalActions = actions && actions.length > 0 ? actions : defaultActions;

  // Google-style variant
  if (variant === 'google') {
    return (
      <article className="ai360-result-google">
        {/* Title as clickable link */}
        <h3 className={clsx('ai360-result-title', highlighted && 'ai360-result-title--highlighted')}>
          <a href={url} target="_blank" rel="noopener noreferrer">
            {truncatedTitle}
          </a>
        </h3>

        {/* URL */}
        {url && (
          <a href={url} target="_blank" rel="noopener noreferrer" className="ai360-result-url">
            {extractDomain(url)}
          </a>
        )}

        {/* Date if available */}
        {date && (
          <div className="ai360-result-date">{date}</div>
        )}

        {/* Description with line clamp */}
        <div className="ai360-result-description">
          {truncatedDescription}
        </div>

        {/* Tags */}
        {tags && tags.length > 0 && (
          <div className="ai360-result-card-enhanced-tags" style={{ marginTop: '8px' }}>
            <TagChips tags={tags} maxVisible={4} />
          </div>
        )}

        {/* TrustScore and Actions */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px' }}>
          {/* Actions */}
          <div className="ai360-result-actions">
            <a 
              href={url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="ai360-action-visit"
            >
              <span>Visitar sitio</span>
              <svg width="16" height="16" viewBox="0 0 16 16">
                <path d="M12 4L4 12M12 4H7m5 0v5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
            </a>
            <button className="ai360-action-more">
              Ver más ▼
            </button>
          </div>

          {/* TrustScore badge */}
          {trustScore !== undefined && trustScore > 0 && (
            <div style={{ flexShrink: 0 }}>
              <TrustScoreBadge score={trustScore} size="small" showLabel={false} />
            </div>
          )}
        </div>
      </article>
    );
  }

  // Original enhanced card variant
  // Build inline styles based on card design settings
  const cardStyles: React.CSSProperties = {
    borderRadius: `${cardBorderRadius}px`,
  };
  
  if (!cardShowBorder) {
    cardStyles.border = '0';
    cardStyles.borderWidth = '0';
  }
  
  if (!cardShowShadow) {
    cardStyles.boxShadow = 'none';
  }

  return (
    <article
      className={clsx(
        'ai360-result-card-enhanced',
        `ai360-result-card-enhanced--${variant}`,
        `ai360-result-card-enhanced--${layoutVariant}`,
        url && !finalActions?.length && 'ai360-result-card-enhanced--clickable',
        !cardShowBorder && 'ai360-result-card--no-border',
        !cardShowShadow && 'ai360-result-card--no-shadow'
      )}
      style={cardStyles}
      onClick={url && !finalActions?.length ? handleCardClick : undefined}
    >
      {/* Header with icon, title, and TrustScore badge */}
      <div className="ai360-result-card-enhanced-header">
        <div className="ai360-result-card-enhanced-header-content">
          {icon && (
            <span
              className="ai360-result-card-enhanced-icon"
              role="img"
              aria-label="card icon"
            >
              {icon}
            </span>
          )}
          <h3 className="ai360-result-card-enhanced-title">{truncatedTitle}</h3>
        </div>

        {trustScore !== undefined && trustScore > 0 && (
          <div className="ai360-result-card-enhanced-trust">
            <TrustScoreBadge score={trustScore} size="small" showLabel={false} />
          </div>
        )}
      </div>

      {/* Description */}
      <div className="ai360-result-card-enhanced-body">
        <p className="ai360-result-card-enhanced-description">{truncatedDescription}</p>
      </div>

      {/* Tags */}
      {tags && tags.length > 0 && (
        <div className="ai360-result-card-enhanced-tags">
          <TagChips tags={tags} maxVisible={3} />
        </div>
      )}

      {/* Action buttons */}
      {finalActions && finalActions.length > 0 && (
        <div className="ai360-result-card-enhanced-footer">
          <ActionButtons actions={finalActions} />
        </div>
      )}
    </article>
  );
};
